import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fileUrl } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Play, EnvelopeSimple, WhatsappLogo, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";
import { STAGES, STAGE_LABELS, SITE_CLASS, AGENT_LABELS } from "@/lib/prospect";
import { ConvertBox } from "@/components/prospect/ConvertBox";
import { DemoReview } from "@/components/prospect/DemoReview";
import { OwnerPhotos } from "@/components/prospect/OwnerPhotos";

const PLACEHOLDER_RE = /\[[^\]\n]{1,80}\]|\{\{?[^}\n]{1,60}\}?\}|<[^>\n]{1,60}>|X{3,}|_{4,}/;

const words = (t) => (t || "").trim().split(/\s+/).filter(Boolean).length;
const SCORE_LABELS = { design: "Design", mobile: "Telemóvel", speed: "Velocidade", seo: "SEO básico", contact: "Contacto/WhatsApp", booking: "Reservas online" };

function Section({ title, children, testid }) {
  return <section className="space-y-2 rounded-2xl border border-border p-4" data-testid={testid}><h3 className="font-display font-bold">{title}</h3>{children}</section>;
}

function Analysis({ a }) {
  if (!a) return <p className="text-sm text-muted-foreground">Ainda não analisado.</p>;
  return (
    <div className="space-y-2 text-sm">
      <div className="font-semibold" data-testid="analysis-total">{a.total != null ? `Nota total: ${Math.round(a.total)}/100` : "Sem site próprio"} · {a.qualified ? "qualificado" : "não qualificado"} (limite {a.threshold})</div>
      {a.scores && <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">{Object.entries(a.scores).map(([k, v]) => <div key={k} className="rounded-lg bg-secondary px-2 py-1">{SCORE_LABELS[k] || k}: <b>{Math.round(v)}</b></div>)}</div>}
      <ol className="list-decimal pl-5" data-testid="analysis-problems">{(a.problems || []).map((p) => <li key={p}>{p}</li>)}</ol>
      {a.pagespeed?.error && <p className="text-xs text-destructive">{a.pagespeed.error}</p>}
      {a.pagespeed?.screenshot_id && <img src={fileUrl(a.pagespeed.screenshot_id)} alt="Captura do site atual" className="max-h-72 rounded-lg border" data-testid="analysis-screenshot" />}
      {a.content?.services?.length > 0 && <p className="text-xs text-muted-foreground">Conteúdo real extraído: {a.content.services.length} serviços/pratos</p>}
    </div>
  );
}

function Messages({ p, onSaved }) {
  const [m, setM] = useState({ email: p.email || "", whatsapp_number: p.whatsapp_number || "", ...p.messages });
  const msgKey = JSON.stringify([p.id, p.email, p.whatsapp_number, p.messages]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setM({ email: p.email || "", whatsapp_number: p.whatsapp_number || "", ...p.messages }); }, [msgKey]);
  const set = (k) => (e) => setM({ ...m, [k]: e.target.value });
  const save = async (quiet = false) => {
    try {
      await api.put(`/prospect/prospects/${p.id}`, { email: m.email, whatsapp_number: m.whatsapp_number, email_subject: m.email_subject, email_body: m.email_body, whatsapp: m.whatsapp });
      if (!quiet) { toast.success("Guardado"); onSaved(); }
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  const act = async (path, msg) => {
    if (!(await save(true))) return false;
    try { await api.post(`/prospect/prospects/${p.id}/${path}`); toast.success(msg); onSaved(); return true; } catch (e) { toast.error(apiError(e)); return false; }
  };
  const openWa = async () => {
    const w = window.open("", "_blank");
    const ok = await act("whatsapp-sent", "WhatsApp aberto");
    const { data } = ok ? await api.get(`/prospect/prospects/${p.id}`).catch(() => ({ data: {} })) : { data: {} };
    if (data.wa_link) {
      if (w) w.location.href = data.wa_link; else window.location.href = data.wa_link;
    } else if (w) w.close();
  };
  if (!p.messages) return <p className="text-sm text-muted-foreground">O Prospector ainda não escreveu as mensagens.</p>;
  const dirty = [m.email_subject, m.email_body, m.whatsapp].some((t) => PLACEHOLDER_RE.test(t || ""));
  const blockers = [!p.demo_approved && "Aprove primeiro a demonstração (secção Designer).", dirty && "O texto tem marcadores por preencher, ex.: [link].", ...(p.message_problems || [])].filter(Boolean);
  const canSend = blockers.length === 0;
  return (
    <div className="space-y-2 text-sm" data-testid="messages-editor">
      <div className="grid gap-2 sm:grid-cols-2">
        <div><Label>Email do negócio</Label><Input value={m.email} onChange={set("email")} data-testid="msg-email-to" /></div>
        <div><Label>Número WhatsApp</Label><Input value={m.whatsapp_number} placeholder={p.phone} onChange={set("whatsapp_number")} data-testid="msg-wa-number" /></div>
      </div>
      <Label>Assunto</Label><Input value={m.email_subject || ""} onChange={set("email_subject")} data-testid="msg-subject" />
      <Label>Email ({words(m.email_body)} palavras, máx. 150)</Label><Textarea rows={9} value={m.email_body || ""} onChange={set("email_body")} data-testid="msg-body" />
      <Label>WhatsApp ({words(m.whatsapp)} palavras, máx. 80)</Label><Textarea rows={5} value={m.whatsapp || ""} onChange={set("whatsapp")} data-testid="msg-whatsapp" />
      {blockers.length > 0 && <ul className="list-disc rounded-lg bg-amber-50 py-2 pl-7 pr-3 text-xs text-amber-900" data-testid="msg-blockers">{[...new Set(blockers)].map((b) => <li key={b}>{b}</li>)}</ul>}
      <p className="text-xs text-muted-foreground">Nada é enviado sem o seu clique. Guarde o texto depois de editar.</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => save()} data-testid="msg-save-btn">Guardar texto</Button>
        <Button size="sm" disabled={!canSend || !m.email} onClick={() => window.confirm(`Aprovar e enviar o email para ${m.email}?`) && act("send-email", "Email enviado")} data-testid="msg-send-email-btn"><EnvelopeSimple className="mr-1" /> Aprovar e enviar email</Button>
        <Button size="sm" variant="outline" disabled={!canSend} onClick={openWa} data-testid="msg-open-wa-btn"><WhatsappLogo className="mr-1" /> Abrir WhatsApp</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => window.confirm("Respondeu NÃO? Fica na lista de exclusão para sempre e a demonstração é apagada.") && act("opt-out", "Adicionado à lista de exclusão")} data-testid="msg-optout-btn"><Prohibit className="mr-1" /> Respondeu NÃO</Button>
      </div>
    </div>
  );
}

export function ProspectSheet({ pid, onClose, onChange }) {
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get(`/prospect/prospects/${pid}`).then(({ data }) => { setP(data); onChange?.(); }), [pid, onChange]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!p?.running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [p?.running, load]);
  const run = async (agent) => {
    try { await api.post(`/prospect/prospects/${pid}/run/${agent}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const setStage = async (stage) => {
    try { await api.put(`/prospect/prospects/${pid}`, { stage }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="prospect-sheet">
        {!p ? <CircleNotch className="animate-spin" /> : (
          <div className="space-y-4">
            <SheetHeader><SheetTitle data-testid="prospect-name">{p.name}</SheetTitle></SheetHeader>
            <div className="text-sm text-muted-foreground">{[p.address, p.phone, SITE_CLASS[p.site_class]].filter(Boolean).join(" · ")} {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="underline">{p.website}</a>}</div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={p.stage} onValueChange={setStage} disabled={p.stage === "cliente"}>
                <SelectTrigger className="w-64" data-testid="prospect-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.filter((s) => s !== "cliente" || p.stage === "cliente").map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
              {["analista", "designer", "prospector"].map((a) => (
                <Button key={a} size="sm" variant="outline" disabled={!!p.running || ["cliente", "descartado"].includes(p.stage)} onClick={() => run(a)} data-testid={`run-${a}-btn`}>
                  {p.running === a ? <CircleNotch className="mr-1 animate-spin" /> : <Play className="mr-1" />} {AGENT_LABELS[a]}
                </Button>
              ))}
            </div>
            {p.error && <p className="rounded-lg bg-red-50 p-2 text-sm text-red-700" data-testid="prospect-error">{p.error}</p>}
            <Section title="Agente Analista" testid="section-analista"><Analysis a={p.analysis} /></Section>
            <Section title="Agente Designer" testid="section-designer"><DemoReview p={p} onChange={load} /></Section>
            <Section title="Fotos enviadas pelo restaurante" testid="section-owner-photos"><OwnerPhotos p={p} onChange={load} /></Section>
            <Section title="Agente Prospector" testid="section-prospector"><Messages p={p} onSaved={load} /></Section>
            {p.demo && <Section title="Converter em cliente GestaCV" testid="section-convert"><ConvertBox p={p} onDone={load} /></Section>}
            <Section title="Histórico dos agentes" testid="section-history">
              <ul className="space-y-1 text-sm">{[...(p.history || [])].reverse().map((h, i) => <li key={i}><b className="capitalize">{h.agent}</b> · {new Date(h.at).toLocaleString("pt-PT")} — {h.summary}</li>)}</ul>
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
